import { create } from "zustand";
import type {
  WordCollection,
  WordCollectionWithStatus,
} from "../../features/wordCollections";

// Game flow and meta state - initialization, challenges, word collections
export interface GameFlowState {
  // Initialization state
  isLoadingData: boolean;
  errorLoadingData: string | null;
  initialDataLoaded: boolean;

  // Word Collections
  wordCollections: WordCollectionWithStatus[];
  activeWordCollections: WordCollection[];

  // Challenge management
  hasPendingChallenge: boolean;
  pendingChallengeWords: { startWord: string; targetWord: string } | null;
  shouldRestoreTempGame: boolean;

  // Global messaging
  globalMessageVisible: boolean;
  globalMessage: string;

  // Actions
  setLoadingState: (loading: boolean, error?: string | null) => void;
  setInitialDataLoaded: (loaded: boolean) => void;
  setWordCollections: (
    collections: WordCollectionWithStatus[],
    active: WordCollection[],
  ) => void;
  setShouldRestoreTempGame: (restore: boolean) => void;
  setHasPendingChallenge: (hasPending: boolean) => void;
  setPendingChallengeWords: (
    words: { startWord: string; targetWord: string } | null,
  ) => void;
  showGlobalMessage: (message: string) => void;
  hideGlobalMessage: () => void;
  reset: () => void;
}

const initialGameFlowData = {
  isLoadingData: false,
  errorLoadingData: null,
  initialDataLoaded: false,
  wordCollections: [],
  activeWordCollections: [],
  hasPendingChallenge: false,
  pendingChallengeWords: null,
  shouldRestoreTempGame: false,
  globalMessageVisible: false,
  globalMessage: "",
};

export const useGameFlowStore = create<GameFlowState>((set) => ({
  ...initialGameFlowData,

  setLoadingState: (loading, error = null) =>
    set({ isLoadingData: loading, errorLoadingData: error }),

  setInitialDataLoaded: (loaded) => set({ initialDataLoaded: loaded }),

  setWordCollections: (collections, active) =>
    set({ wordCollections: collections, activeWordCollections: active }),

  setHasPendingChallenge: (hasPending) =>
    set({ hasPendingChallenge: hasPending }),

  setPendingChallengeWords: (words) => set({ pendingChallengeWords: words }),

  setShouldRestoreTempGame: (restore) =>
    set({ shouldRestoreTempGame: restore }),

  showGlobalMessage: (message) =>
    set({ globalMessage: message, globalMessageVisible: true }),

  hideGlobalMessage: () =>
    set({ globalMessageVisible: false, globalMessage: "" }),
  reset: () => set(initialGameFlowData),
}));
